import { useEffect, useRef } from "react";
import UserService from "src/services/UserService";
import { getStorage } from "src/utils/utils";

const REIDENTIFY_THROTTLE_MS = 15000;

const getWidgetShadow = () => {
  if (typeof document === "undefined") return null;
  const widgetRoot = document.querySelector(".omni-root");
  if (widgetRoot?.shadowRoot) return widgetRoot.shadowRoot;

  for (const element of document.querySelectorAll("*")) {
    if (element.shadowRoot?.querySelector(".omni-panel")) {
      return element.shadowRoot;
    }
  }
  return null;
};

const widgetNeedsIdentity = () => {
  const shadow = getWidgetShadow();
  if (!shadow) return true;
  return Boolean(shadow.querySelector(".omni-cta")) || !shadow.querySelector(".omni-foot");
};

const rememberIdentityAttempt = (token) => {
  try {
    sessionStorage.setItem("omniIdentityForToken", token);
  } catch {
    // sessionStorage can be unavailable in restricted browser modes.
  }
};

const identifyWidget = async (identityToken) => {
  const api = window.omniChat;
  if (!api || !identityToken) return false;
  if (typeof api === "function") {
    const result = api("identify", { identity_token: identityToken });
    if (result && typeof result.then === "function") await result;
    return true;
  }
  if (typeof api.identify === "function") {
    const result = api.identify({ identity_token: identityToken });
    if (result && typeof result.then === "function") await result;
    return true;
  }
  return false;
};

export default function OmniChatIdentity() {
  const lastIdentifiedTokenRef = useRef("");
  const lastAttemptAtRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;

    const sync = async () => {
      if (cancelled || inFlight || typeof window === "undefined") return;
      const token = getStorage("token");
      const memberId = getStorage("memberId");
      if (!token || !memberId || !window.omniChat) return;

      const needsIdentity =
        lastIdentifiedTokenRef.current !== token || widgetNeedsIdentity();
      const now = Date.now();
      if (!needsIdentity) return;
      if (
        lastIdentifiedTokenRef.current === token &&
        now - lastAttemptAtRef.current < REIDENTIFY_THROTTLE_MS
      ) {
        return;
      }
      lastAttemptAtRef.current = now;

      inFlight = true;
      try {
        const response = await UserService.getOmniIdentity();
        const identityToken = response?.data?.identity_token;
        if (await identifyWidget(identityToken)) {
          lastIdentifiedTokenRef.current = token;
          rememberIdentityAttempt(token);
        }
      } catch {
        // Keep the member site quiet; operators will still see login CTA until the next valid token.
      } finally {
        inFlight = false;
      }
    };

    sync();
    const timer = window.setInterval(sync, 4000);
    window.addEventListener("focus", sync);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", sync);
    };
  }, []);

  return null;
}
